import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Button,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageSrc } from '../../utils/imageHelper';
import { getColorFromName } from '../../utils/colorUtils';
import { COLORS } from '../../theme/tokens';
import useFilterOptions, {
  parseFiltersFromUrl,
  buildFilterUrl,
} from './useFilterOptions';

const isColorFilter = (title) => {
  const lower = title.toLowerCase();
  return lower.includes('color') || lower.includes('colour');
};

const ProductFilters = ({ onApplyFilters, onClearFilters }) => {
  const { t } = useTranslation();
  const { filters, loading, error } = useFilterOptions();
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedFilters(parseFiltersFromUrl(location.search));
  }, [location.search]);

  const applyFiltersToUrl = (filterState) => {
    try {
      setIsApplyingFilters(true);

      if (onApplyFilters) {
        onApplyFilters();
      }

      navigate(buildFilterUrl(location.pathname, location.search, filterState), { replace: true });

      setTimeout(() => {
        setIsApplyingFilters(false);
      }, 500);
    } catch (applyError) {
      console.error('Error applying filters:', applyError);
      setIsApplyingFilters(false);
    }
  };

  const handleFilterChange = (parentId, childId) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      if (!newFilters[parentId]) {
        newFilters[parentId] = [];
      }

      if (newFilters[parentId].includes(childId)) {
        newFilters[parentId] = newFilters[parentId].filter((id) => id !== childId);
        if (newFilters[parentId].length === 0) {
          delete newFilters[parentId];
        }
      } else {
        newFilters[parentId] = [...newFilters[parentId], childId];
      }

      setTimeout(() => {
        applyFiltersToUrl(newFilters);
      }, 0);

      return newFilters;
    });
  };

  const clearSectionFilters = (filterId) => {
    const newFilters = { ...selectedFilters };
    delete newFilters[filterId];
    setSelectedFilters(newFilters);
    setTimeout(() => {
      applyFiltersToUrl(newFilters);
    }, 0);
  };

  const clearFilters = () => {
    try {
      setIsApplyingFilters(true);
      setSelectedFilters({});

      const searchParams = new URLSearchParams(location.search);

      for (const key of [...searchParams.keys()]) {
        if (key.startsWith('filter_')) {
          searchParams.delete(key);
        }
      }

      searchParams.set('page', '1');

      if (onClearFilters) {
        onClearFilters();
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

      setTimeout(() => {
        setIsApplyingFilters(false);
      }, 500);
    } catch (clearError) {
      console.error('Error clearing filters:', clearError);
      setIsApplyingFilters(false);
    }
  };

  const hasSelectedFilters = Object.keys(selectedFilters).length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="body2">{t('shop.errorLoadingFilters')}</Typography>
      </Box>
    );
  }

  if (filters.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, position: 'relative' }}>
      {isApplyingFilters && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 2,
            borderRadius: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {filters.map((filter) => {
        const colorFilter = isColorFilter(filter.title);
        const sectionSelected = selectedFilters[filter.id]?.length > 0;

        return (
          <Box
            key={filter.id}
            sx={{
              borderBottom: `1px solid ${COLORS.borderHairline}`,
              pb: '20px',
              mb: '20px',
              opacity: isApplyingFilters ? 0.6 : 1,
              pointerEvents: isApplyingFilters ? 'none' : 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  color: COLORS.textPrimary,
                }}
              >
                {filter.title}
              </Typography>
              {sectionSelected && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => clearSectionFilters(filter.id)}
                  sx={{
                    minWidth: 'auto',
                    p: '0 8px',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    color: COLORS.textSecondary,
                    textDecoration: 'underline',
                  }}
                  disabled={isApplyingFilters}
                >
                  {t('shop.clear')}
                </Button>
              )}
            </Box>

            {colorFilter ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px 16px',
                }}
              >
                {filter.children.map((child) => {
                  const isSelected = selectedFilters[filter.id]?.includes(child.attribute_id) || false;

                  return (
                    <Box
                      key={child.attribute_id}
                      component="button"
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => handleFilterChange(filter.id, child.attribute_id)}
                      disabled={isApplyingFilters}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        p: 0,
                        textAlign: 'left',
                      }}
                    >
                      <Box
                        component="span"
                        aria-hidden="true"
                        data-testid={`color-swatch-${child.attribute_id}`}
                        sx={{
                          display: 'inline-block',
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          flexShrink: 0,
                          border: `1px solid ${COLORS.borderControl}`,
                          backgroundImage: child.attribute_image
                            ? `url(${getImageSrc(child.attribute_image, 'thumbs')})`
                            : 'none',
                          backgroundColor: child.attribute_image
                            ? 'transparent'
                            : getColorFromName(child.displayTitle),
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          boxShadow: isSelected
                            ? `0 0 0 2px #FFF, 0 0 0 3.5px ${COLORS.green}`
                            : 'none',
                        }}
                      />
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: COLORS.textPrimary,
                        }}
                      >
                        {child.displayTitle}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <FormGroup>
                {filter.children.map((child) => (
                  <FormControlLabel
                    key={child.attribute_id}
                    control={
                      <Checkbox
                        checked={selectedFilters[filter.id]?.includes(child.attribute_id) || false}
                        onChange={() => handleFilterChange(filter.id, child.attribute_id)}
                        disabled={isApplyingFilters}
                        sx={{
                          p: '4px',
                          '& .MuiSvgIcon-root': {
                            width: 16,
                            height: 16,
                            borderRadius: '2px',
                          },
                          color: COLORS.checkboxStroke,
                          '&.Mui-checked': {
                            color: COLORS.green,
                          },
                          '& .MuiSvgIcon-root path': {
                            strokeWidth: 1.5,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" component="span" sx={{ fontSize: '0.8125rem' }}>
                        {child.displayTitle}
                      </Typography>
                    }
                    sx={{ ml: 0, mb: 0.5 }}
                  />
                ))}
              </FormGroup>
            )}
          </Box>
        );
      })}

      {hasSelectedFilters && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={clearFilters}
            disabled={isApplyingFilters}
            startIcon={isApplyingFilters ? <CircularProgress size={16} /> : null}
          >
            {t('shop.clearAllFilters')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProductFilters;
